import * as Blockly from 'blockly/core';
import { BLOCK_TEXT_COLOURS } from '@/(routers)/boards/editor/block/blockRegistry';
import { blockSound } from '@/(routers)/boards/editor/block/blockSound';

export interface DropdownOption {
    value: string;
    label?: string;
    imagePath?: string;
    alt?: string;
    tooltip?: string;
}

const IMG_SIZE = 24;
const BLOCK_IMG_SIZE = 32;
const PADDING = 6;
const FIELD_SIZE = IMG_SIZE + PADDING * 3;
const ITEM_SIZE = 36;
const ARROW_WIDTH = 10;
const ARROW_HEIGHT = 7;
const ARROW_MARGIN_LEFT = 0;
const ARROW_MARGIN_RIGHT = 7;
const ARROW_SIZE = ARROW_WIDTH + ARROW_MARGIN_LEFT + ARROW_MARGIN_RIGHT;

const SMALL_FIELD_SIZE = 32;
const SMALL_FONT_SIZE = '13px';
const SMALL_PADDING = 2;
const SMALL_ARROW_WIDTH = 7;
const SMALL_ARROW_HEIGHT = 5;
const SMALL_ARROW_MARGIN_RIGHT = 5;
const SMALL_ARROW_SIZE = SMALL_ARROW_WIDTH + SMALL_ARROW_MARGIN_RIGHT;

export class FieldOperatorDropdown extends Blockly.Field<string> {
    private options_: DropdownOption[];
    private selectedValue_: string;
    private imageEl_: SVGImageElement | null = null;
    private labelEl_: SVGTextElement | null = null;
    private isOpen_ = false;
    private isDisabled_ = false;
    private tooltipEl_: HTMLDivElement | null = null;
    private rectColour_: string | null = null;
    private onValueChange_: ((value: string) => void) | null = null;
    private size_mode_: 'normal' | 'small';

    static override fromJson(
        options: Blockly.FieldConfig & { options: DropdownOption[]; value?: string },
    ): FieldOperatorDropdown {
        return new FieldOperatorDropdown(options.options, options.value);
    }

    constructor(
        options: DropdownOption[],
        valueOrValidator?: string | Function,
        validator?: Function,
        rectColour?: string,
        sizeMode?: 'normal' | 'small',
    ) {
        const value = typeof valueOrValidator === 'string' ? valueOrValidator : options[0]?.value ?? '';
        const fn = typeof valueOrValidator === 'function' ? valueOrValidator : validator;
        super(value);
        this.options_ = options;
        this.selectedValue_ = value;
        this.SERIALIZABLE = true;
        this.rectColour_ = rectColour ?? null;
        this.size_mode_ = sizeMode ?? 'normal';
        if (fn) this.setValidator(fn as any);
    }

    private isSmall_(): boolean {
        return this.size_mode_ === 'small';
    }

    private fieldHeight_(): number {
        return this.isSmall_() ? SMALL_FIELD_SIZE : FIELD_SIZE;
    }

    private fieldPadding_(): number {
        return this.isSmall_() ? SMALL_PADDING : PADDING;
    }

    private arrowSize_(): number {
        return this.isSmall_() ? SMALL_ARROW_SIZE : ARROW_SIZE;
    }

    private getBlockColour_(): string {
        return this.rectColour_ ?? this.getSourceBlock()?.getColour() ?? '#0091EA';
    }

    private applyRectStyle_(active: boolean, hover = false): void {
        if (!this.borderRect_) return;

        if (this.isDisabled_) {
            this.borderRect_.setAttribute(
                'style',
                `fill: var(--blocklyDisabledPattern) !important; fill-opacity: 0.5; stroke: rgba(0,0,0,0.1); stroke-width: 1;`,
            );
            return;
        }

        const colour = this.getBlockColour_();
        const fill = active ? 'rgba(0,0,0,0.2)' : colour;
        const stroke = hover ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.3)';
        const strokeWidth = hover ? 2 : 1;
        this.borderRect_.setAttribute(
            'style',
            `fill: ${fill} !important; stroke: ${stroke}; stroke-width: ${strokeWidth};`,
        );
    }

    public refreshStyle(): void {
        this.applyRectStyle_(false);
    }

    public setRectColour(colour: string): void {
        this.rectColour_ = colour;
        this.applyRectStyle_(false);
    }

    public setDisabled(disabled: boolean): void {
        this.isDisabled_ = disabled;
        this.applyRectStyle_(false);
    }

    public setOnValueChange(cb: (value: string) => void): void {
        this.onValueChange_ = cb;
    }

    public setOptions(options: DropdownOption[]): void {
        this.options_ = options;
        if (!this.options_.find((o) => o.value === this.selectedValue_)) {
            this.selectedValue_ = this.options_[0]?.value ?? '';
        }
        this.updateDisplay_();
        this.refreshSize_();
    }

    private hasImage_(): boolean {
        return this.options_?.some((o) => o.imagePath) ?? false;
    }

    private buildArrowPath_(arrowX: number): string {
        const aw = this.isSmall_() ? SMALL_ARROW_WIDTH : ARROW_WIDTH;
        const ah = this.isSmall_() ? SMALL_ARROW_HEIGHT : ARROW_HEIGHT;
        const fh = this.fieldHeight_();
        const arrowY = fh / 2;
        const r = 2;
        const ax = arrowX;
        const bx = arrowX + aw;
        const cx = arrowX + aw / 2;
        const ty = arrowY - ah / 2;
        const by = arrowY + ah / 2;
        return `M ${ax + r},${ty} L ${bx - r},${ty} Q ${bx},${ty} ${bx - r / 2},${ty + r} L ${cx + r / 2},${
            by - r
        } Q ${cx},${by} ${cx - r / 2},${by - r} L ${ax + r / 2},${ty + r} Q ${ax},${ty} ${ax + r},${ty} Z`;
    }

    private refreshSize_(): void {
        if (!this.options_ || this.hasImage_()) return;
        const w = this.calcTextWidth_() + this.arrowSize_() + this.fieldPadding_() * 2;
        this.borderRect_?.setAttribute('width', String(w));

        const arrow = this.fieldGroup_?.querySelector('path');
        if (arrow) {
            const arrowMarginRight = this.isSmall_() ? SMALL_ARROW_MARGIN_RIGHT : ARROW_MARGIN_RIGHT;
            const arrowW = this.isSmall_() ? SMALL_ARROW_WIDTH : ARROW_WIDTH;
            const arrowX = w - arrowMarginRight - arrowW;
            arrow.setAttribute('d', this.buildArrowPath_(arrowX));
        }
    }

    private calcTextWidth_(): number {
        if (this.labelEl_) {
            const len = this.labelEl_.getComputedTextLength();
            if (len > 0) return len + this.fieldPadding_() + 4;
        }
        const selected = this.options_?.find((o) => o.value === this.selectedValue_);
        const text = selected?.label ?? '';
        const charW = this.isSmall_() ? 10 : 13;
        return Math.max(30, text.length * charW);
    }

    private calcDropdownWidth_(): number {
        const maxLen = Math.max(
            ...this.options_.map((o) => {
                const text = o.label ?? o.value;
                const width = [...text].reduce((acc, char) => {
                    return acc + (char.charCodeAt(0) > 0x7f ? 2 : 1);
                }, 0);
                return width;
            }),
        );
        return Math.max(60, maxLen * 6 + 20);
    }

    private createTooltip_(): HTMLDivElement {
        const el = document.createElement('div');
        el.style.cssText = [
            'position:fixed',
            'pointer-events:none',
            'z-index:9999',
            'padding:6px 10px',
            'border-radius:8px',
            'background:rgba(29,40,77,0.95)',
            'color:white',
            'font-size:13px',
            'font-weight:500',
            "font-family:'Noto Sans JP',sans-serif",
            'white-space:nowrap',
            'opacity:0',
            'transition:opacity 120ms ease',
            'box-shadow:0 2px 8px rgba(0,0,0,0.3)',
            'letter-spacing:0.03em',
        ].join(';');
        document.body.appendChild(el);
        document.addEventListener('scroll', () => this.hideTooltip_(), { passive: true, capture: true });
        document.addEventListener('pointerdown', () => this.hideTooltip_(), { passive: true });
        return el;
    }

    private showTooltip_(text: string, x: number, y: number): void {
        if (!this.tooltipEl_) this.tooltipEl_ = this.createTooltip_();
        this.tooltipEl_.textContent = text;
        this.tooltipEl_.style.left = `${x + 12}px`;
        this.tooltipEl_.style.top = `${y - 32}px`;
        this.tooltipEl_.style.opacity = '1';
    }

    private getTextColour_(): string {
        const type = this.getSourceBlock()?.type ?? '';
        return BLOCK_TEXT_COLOURS[type] ?? 'white';
    }

    private hideTooltip_(): void {
        if (this.tooltipEl_) this.tooltipEl_.style.opacity = '0';
    }

    override initView(): void {
        this.fieldGroup_?.classList.add('blocklyOperatorField');

        const isImageMode = this.hasImage_();
        const fh = this.fieldHeight_();
        const fp = this.fieldPadding_();
        const as_ = this.arrowSize_();
        const fieldWidth = isImageMode ? FIELD_SIZE + ARROW_SIZE : this.calcTextWidth_() + as_ + fp * 2;

        this.borderRect_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 6, ry: 6, x: 0, y: 0, width: fieldWidth, height: fh },
            this.fieldGroup_,
        );

        if (isImageMode) {
            this.imageEl_ = Blockly.utils.dom.createSvgElement(
                Blockly.utils.Svg.IMAGE,
                {
                    height: BLOCK_IMG_SIZE,
                    width: BLOCK_IMG_SIZE,
                    x: (FIELD_SIZE - BLOCK_IMG_SIZE) / 2,
                    y: (FIELD_SIZE - BLOCK_IMG_SIZE) / 2,
                },
                this.fieldGroup_,
            ) as SVGImageElement;
        } else {
            const fontSize = this.isSmall_() ? SMALL_FONT_SIZE : '17px';
            const textY = this.isSmall_() ? fh / 2 + 5 : fh / 2 + 7;
            this.labelEl_ = Blockly.utils.dom.createSvgElement(
                Blockly.utils.Svg.TEXT,
                {
                    x: fp + 4,
                    y: textY,
                    'font-size': fontSize,
                    'font-weight': '500',
                    'font-family': 'Corporate Logo, sans-serif',
                    style: `fill: ${this.getTextColour_()} !important;`,
                },
                this.fieldGroup_,
            ) as SVGTextElement;
        }

        const arrowMarginRight = this.isSmall_() ? SMALL_ARROW_MARGIN_RIGHT : ARROW_MARGIN_RIGHT;
        const arrowW = this.isSmall_() ? SMALL_ARROW_WIDTH : ARROW_WIDTH;
        const arrowX = isImageMode ? FIELD_SIZE + ARROW_MARGIN_LEFT : fieldWidth - arrowMarginRight - arrowW;
        Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.PATH,
            { d: this.buildArrowPath_(arrowX), fill: this.getTextColour_() },
            this.fieldGroup_,
        );

        this.updateDisplay_();

        requestAnimationFrame(() => {
            this.applyRectStyle_(false);
            this.refreshSize_();
            this.updateDisplay_();
            this.onValueChange_?.(this.selectedValue_);

            this.fieldGroup_?.addEventListener('mouseenter', () => {
                if (!this.isOpen_ && !this.isDisabled_) this.applyRectStyle_(false, true);
            });
            this.fieldGroup_?.addEventListener('mouseleave', () => {
                if (!this.isOpen_) this.applyRectStyle_(false, false);
            });
        });
    }

    private updateDisplay_(): void {
        if (!this.imageEl_ && !this.labelEl_) return;
        const opt = this.options_.find((o) => o.value === this.selectedValue_);
        if (!opt) return;

        if (this.imageEl_ && opt.imagePath) {
            this.imageEl_.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', opt.imagePath);
        }
        if (this.labelEl_ && opt.label) {
            this.labelEl_.textContent = opt.label;
            this.labelEl_.style.fill = this.getTextColour_();
        }

        const arrow = this.fieldGroup_?.querySelector('path');
        if (arrow) arrow.setAttribute('fill', this.getTextColour_());
    }

    override getValue(): string {
        return this.selectedValue_;
    }

    override doValueUpdate_(newValue: string): void {
        this.selectedValue_ = newValue;
        this.updateDisplay_();
        this.refreshSize_();
        this.onValueChange_?.(newValue);
        if (!this.isOpen_) {
            this.applyRectStyle_(false);
        }
    }

    override getSize(): Blockly.utils.Size {
        const fh = this.fieldHeight_();
        const w = this.hasImage_()
            ? FIELD_SIZE + ARROW_SIZE
            : this.calcTextWidth_() + this.arrowSize_() + this.fieldPadding_() * 2;
        return new Blockly.utils.Size(w, fh);
    }

    override showEditor_(): void {
        if (this.isDisabled_) return;

        const blockColour = this.getBlockColour_();
        const isImageMode = this.hasImage_();
        const dropdownWidth = this.calcDropdownWidth_();

        this.isOpen_ = true;
        this.applyRectStyle_(true);

        const dropdownDiv = Blockly.DropDownDiv.getContentDiv();
        dropdownDiv.innerHTML = '';

        const container = document.createElement('div');
        container.style.cssText = `
            display: flex;
            flex-direction: column;
            align-items: ${isImageMode ? 'center' : 'stretch'};
            padding: 4px;
            gap: 2px;
            min-width: ${isImageMode ? 'auto' : `${dropdownWidth}px`};
        `;

        for (const opt of this.options_) {
            const isSelected = opt.value === this.selectedValue_;
            const item = document.createElement('div');

            if (isImageMode) {
                item.style.cssText = `
                    width: ${ITEM_SIZE}px;
                    height: ${ITEM_SIZE}px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 6px;
                    cursor: pointer;
                    box-sizing: border-box;
                    background: ${isSelected ? 'rgba(0,0,0,0.2)' : 'transparent'};
                `;
            } else {
                item.style.cssText = `
                    height: ${ITEM_SIZE}px;
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    padding: 0 10px;
                    border-radius: 6px;
                    cursor: pointer;
                    box-sizing: border-box;
                    background: ${isSelected ? 'rgba(0,0,0,0.2)' : 'transparent'};
                `;
            }

            if (isImageMode && opt.imagePath) {
                const img = document.createElement('img');
                img.src = opt.imagePath;
                img.alt = opt.alt ?? '';
                img.width = IMG_SIZE;
                img.height = IMG_SIZE;
                item.appendChild(img);
            } else {
                const span = document.createElement('span');
                span.textContent = opt.label ?? opt.value;
                const textColour = this.getTextColour_();
                span.style.cssText = `
                    color: ${textColour};
                    font-size: 13px;
                    font-weight: 500;
                    font-family: 'Corporate Logo', sans-serif;
                    white-space: nowrap;
                `;
                item.appendChild(span);
            }

            item.addEventListener('mouseenter', (e) => {
                item.style.background = 'rgba(0,0,0,0.15)';
                if (opt.tooltip) this.showTooltip_(opt.tooltip, e.clientX, e.clientY);
            });
            item.addEventListener('mousemove', (e) => {
                if (opt.tooltip) this.showTooltip_(opt.tooltip, e.clientX, e.clientY);
            });
            item.addEventListener('mouseleave', () => {
                item.style.background = isSelected ? 'rgba(0,0,0,0.2)' : 'transparent';
                this.hideTooltip_();
            });
            item.addEventListener('click', () => {
                blockSound.queue('dropdown');
                this.setValue(opt.value);
                Blockly.DropDownDiv.hideIfOwner(this);
            });

            container.appendChild(item);
        }

        dropdownDiv.appendChild(container);

        Blockly.DropDownDiv.setColour(blockColour, 'rgba(0,0,0,0.3)');
        Blockly.DropDownDiv.showPositionedByField(this, () => {
            this.isOpen_ = false;
            this.applyRectStyle_(false);
            this.hideTooltip_();
        });

        requestAnimationFrame(() => {
            if (!Blockly.DropDownDiv.isVisible()) {
                this.isOpen_ = false;
                this.applyRectStyle_(false);
            }
        });
    }
}
